import Button from "@components/Button";
import { Box } from "@components/core/Box";
import { Modal } from "@components/core/Modal";
import { useSessionContext } from "@lib/context/session";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import { Fragment, useState } from "react";
import { BeatbotInfoButtonWrapper, BeatbotModalBody, BeatbotModalTitle, InfoIconStyled } from "./BeatbotInfoButton.style";

const BEATBOT_INFO_CONFIG = {
	HOME_PAGE: {
		translationKey: "Beatbot.BetterRecommendations.MyRecommendations",
		location: "home_page",
	},
	MY_RECOMMENDATIONS: {
		translationKey: "Beatbot.BetterRecommendations.MyRecommendations",
		location: "my_recommendations",
	},
	TRACK_PAGE: {
		translationKey: "Beatbot.BetterRecommendations.TrackPage",
		location: "track_page",
	},
	CART_PAGE: {
		translationKey: "Beatbot.BetterRecommendations.CartPage",
		location: "main_cart",
	},
	CUSTOM_CART_PAGE: {
		translationKey: "Beatbot.BetterRecommendations.CartPage",
		location: "custom_cart",
	},
} as const;

type BeatbotInfoKey = keyof typeof BEATBOT_INFO_CONFIG;

const FEEDBACK_BASE_URL = "https://research.beatport.com/jfe/form/SV_8c9gqRQPD6tSv1I";

interface BeatbotInfoButtonProps {
	contentKey: BeatbotInfoKey;
}

interface BuildFeedbackUrlParams {
	key: BeatbotInfoKey;
	isLoggedIn: boolean;
	username?: string | null;
	firstName?: string | null;
	email?: string | null;
}

const buildFeedbackUrl = ({ key, isLoggedIn, username, firstName, email }: BuildFeedbackUrlParams) => {
	const url = new URL(FEEDBACK_BASE_URL);
	url.searchParams.set("authentication", isLoggedIn ? "yes" : "no");
	url.searchParams.set("source", "store");
	const isServer = typeof window === "undefined";
	url.searchParams.set("url", isServer ? "" : window.location.href);
	url.searchParams.set("location", BEATBOT_INFO_CONFIG[key].location);

	if (isLoggedIn) {
		if (username) url.searchParams.set("username", username);
		if (firstName) url.searchParams.set("first_name", firstName);
		if (email) url.searchParams.set("email", email);
	}

	return url.toString();
};

const BeatbotInfoButton = ({ contentKey }: BeatbotInfoButtonProps) => {
	const { session, getIsSessionValid } = useSessionContext();
	const isLoggedInSessionValid = getIsSessionValid({ isAnonAllowed: false });
	const { t } = useTranslation("translation");
	const [isModalOpen, setIsModalOpen] = useState(false);
	const title = t(`${BEATBOT_INFO_CONFIG[contentKey].translationKey}.Title`);
	const body = t(`${BEATBOT_INFO_CONFIG[contentKey].translationKey}.Body`);

	return (
		<Fragment>
			<Modal
				header={<BeatbotModalTitle>{title}</BeatbotModalTitle>}
				show={isModalOpen}
				showClose
				onClose={() => setIsModalOpen(false)}
				alignSelf="center"
				footer={(
					<Box
						p="0"
						direction="row"
						gap="8"
						alignItems="end"
						justifyContent="end"
						width="100"
					>
						<Button
							type="outline-secondary"
							onClick={() => setIsModalOpen(false)}
						>
							{t("Actions.Cancel")}
						</Button>
						<Link
							target="_blank"
							href={buildFeedbackUrl({
								key: contentKey,
								isLoggedIn: isLoggedInSessionValid,
								username: session?.introspect?.username,
								firstName: session?.introspect?.first_name,
								email: session?.user?.email,
							})}
						>
							<Button type="primary">
								{t("Actions.GiveFeedback")}
							</Button>
						</Link>
					</Box>
				)}
			>
				<BeatbotModalBody>
					{body}
				</BeatbotModalBody>
			</Modal>
			<InfoIconStyled
				id="info"
				role="button"
				aria-label={t(`${BEATBOT_INFO_CONFIG[contentKey].translationKey}.Title`)}
				width="16"
				height="16"
				onClick={() => setIsModalOpen(true)}
			/>
		</Fragment>
	);
};

export { BEATBOT_INFO_CONFIG, BeatbotInfoButtonWrapper };
export default BeatbotInfoButton;
